// src/components/Control/Operator/Trainer.tsx
// Фитнес-тренер (нода trainer, trainer.py на роботе): робот следит за упражнением по камере 1 —
// считает повторы (присед, выпады) или засекает удержание (планка), говорит, что поправить,
// подводит итог подхода и отсчитывает отдых.
//   robot/<id>/trainer/cmd   <- {"cmd":"start","exercise"} | {"cmd":"stop"} | {"cmd":"next"} | {"cmd":"config", ...}
//   robot/<id>/trainer/state -> TrainerState (telemetry), trainer/event -> история событий (events.ts)
// Раздел настроек «Фитнес-тренер» и плашка поверх кадра камеры 1 на время тренировки.
import React, { useEffect, useState } from 'react';
import { useRobot, useTelemetryState, useUiActions } from '../robot/RobotContext';
import {
  STALE, TrainerConfig, TrainerHold, TrainerKind, TrainerRep, TrainerState, TrainerView, TrainerVoice, fresh,
} from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { CameraView } from './Camera';

const VIEW_RU: Record<TrainerView, string> = { side: 'сбоку', front: 'спереди', oblique: 'вполоборота' };
const VOICE_RU: [TrainerVoice, string][] = [
  ['robot', 'в динамик робота'], ['site', 'в этом браузере'], ['both', 'и там, и там'], ['off', 'молча'],
];
// Короткие названия замечаний (полные фразы с советом приходят с робота в phrases)
const FAULT_RU: Record<string, string> = {
  weight_forward: 'гиря уходит вперёд', knees_in: 'колени внутрь', feet_wide: 'стопы слишком широко',
  shallow: 'неглубоко', lean: 'корпус наклонён', heels_up: 'пятки отрываются',
  knee_over_toe: 'колено за носком', hips_low: 'таз провис', hips_high: 'таз поднят',
};
const faultNames = (faults: string[]) => faults.map(f => FAULT_RU[f] ?? f).join(', ');

// Куда встать и что видно с какого ракурса (то же решает робот: squat.py, lunge.py, plank.py)
interface Guide { place: string; side: string; front?: string; notChecked: string }
const GUIDES: Record<string, Guide> = {
  goblet_squat: {
    place: 'Боком к роботу, в 2–3 м, чтобы в кадре были и голова, и стопы; камера — примерно на уровне таза.',
    side: 'глубину, наклон корпуса, гирю над пятками и пятки',
    front: 'колени (не заваливаются ли внутрь) и ширину стойки',
    notChecked: 'разворот носков, скругление поясницы, дыхание',
  },
  lunge: {
    place: 'Боком к роботу, в 2,5–3 м: шаг длинный, в кадре должны быть обе стопы и голова.',
    side: 'глубину (оба колена), переднее колено — не уходит ли за носок, и наклон корпуса',
    notChecked: 'колени внутрь, равновесие, дыхание',
  },
  plank: {
    place: 'Боком к роботу, в 2–3 м, всё тело от головы до пяток в кадре; робот смотрит снизу — так линия тела видна лучше всего.',
    side: 'таз — не провисает ли и не поднимается ли над линией плечо–пятки',
    notChecked: 'положение головы и лопаток, дыхание',
  },
};
const GUIDE_FALLBACK: Guide = { place: 'Боком к роботу, всё тело в кадре.', side: 'технику', notChecked: '' };

function useTrainer() {
  useTick(1000);
  const { session, robotId, notify } = useRobot();
  const { st, at } = useTelemetryState(t => ({ st: t.trainer, at: t.trainerAt }));
  const send = (msg: object) => putJson(session, `robot/${robotId}/trainer/cmd`, msg)
    .then(ok => { if (!ok) notify('Не удалось отправить команду тренеру', 'error'); return ok; });
  return { st: st && fresh(at, STALE.node) ? st : null, send, online: !!session };
}

const kindOf = (st: TrainerState, exercise = st.exercise): TrainerKind =>
  st.exercises[exercise]?.kind ?? (exercise === st.exercise ? st.kind : undefined) ?? 'reps';
const isRep = (x: TrainerRep | TrainerHold | null): x is TrainerRep => !!x && 'n' in x;

const num = (v: number | string | null | undefined, digits = 0) =>
  typeof v !== 'number' ? '—' : v.toLocaleString('ru-RU', { maximumFractionDigits: digits, minimumFractionDigits: digits });

function depthText(thigh: number | string | null | undefined) {
  if (typeof thigh !== 'number') return '—';
  return thigh <= 0 ? `ниже параллели на ${num(-thigh)}°` : `бедро выше параллели на ${num(thigh)}°`;
}

function hipsText(pct: number | string | null | undefined) {
  if (typeof pct !== 'number') return '—';
  return `${pct >= 0 ? 'выше' : 'ниже'} линии на ${num(Math.abs(pct), 1)}% (прямо — около +10%)`;
}

// Итог законченного подхода — в отдых счётчики текущего уже обнулены
const lastSet = (st: TrainerState) => (st.phase === 'rest' ? st.history[st.history.length - 1] : undefined);

// ---------- плашка поверх камеры ----------

export const TrainerBadge: React.FC = () => {
  const { st } = useTrainer();
  const { openSettings } = useUiActions();
  if (!st?.active) return null;
  const hold = kindOf(st) === 'hold';
  const done = lastSet(st);
  const last = isRep(st.last) && st.last.set === st.set ? st.last : null;
  let line: string;
  let warn = false;
  if (st.phase === 'rest') line = `Отдых · ${st.rest_left ?? 0} с`;
  else if (hold) {
    const faults = st.live?.faults ?? [];
    warn = faults.length > 0;
    line = st.phase === 'waiting' ? 'Встаньте в планку боком к роботу' : warn ? faultNames(faults) : 'держим';
  } else if (st.phase === 'waiting') line = st.seen ? 'Замрите на секунду' : 'Встаньте в кадр во весь рост';
  else if (last) {
    warn = last.faults.length > 0;
    line = warn ? faultNames(last.faults) : 'хорошо';
  } else line = st.view ? `Вижу ${VIEW_RU[st.view]} — начинайте` : 'Начинайте';
  const goal = hold ? st.hold_s ?? st.config.hold_s : st.target_reps;
  const value = hold ? Math.floor(done ? done.hold_s ?? 0 : st.hold ?? 0) : done ? done.reps ?? 0 : st.reps;
  return (
    <button type="button" className={`op-trainer-badge ${st.phase} ${warn ? 'warn' : ''}`}
            title="Фитнес-тренер: открыть" onClick={e => { e.stopPropagation(); openSettings('trainer'); }}>
      <span className="op-trainer-badge-top">
        Подход {st.set}/{st.sets}{done ? ' готов' : ''} · <b>{value}</b>/{goal}{hold ? ' с' : ''}
      </span>
      <span className="op-trainer-badge-line" aria-live="polite">{line}</span>
    </button>
  );
};

// ---------- раздел настроек ----------

const PHASE_RU: Record<string, string> = {
  idle: 'не идёт', waiting: 'жду вас', set: 'подход', rest: 'отдых', done: 'окончена',
};

function sessionHint(st: TrainerState, hold: boolean, guide: Guide) {
  if (st.phase === 'rest') return hold ? 'Встанете в планку раньше — подход начнётся сам.' : 'Начнёте раньше — подход начнётся сам.';
  if (st.phase === 'waiting') {
    if (hold) return st.seen ? 'Вижу вас — встаньте в планку, таймер пойдёт сам.' : 'Встаньте в планку боком к роботу, всё тело в кадре камеры 1.';
    return st.seen ? 'Вижу вас — замрите на секунду в стойке (или просто начинайте: со второго повтора робот подхватит).'
      : 'Встаньте боком к роботу, во весь рост в кадре камеры 1.';
  }
  if (!st.seen) return 'Не вижу человека в кадре.';
  if (!st.view) return '';
  if (st.view === 'side') return `Вижу сбоку: проверяю ${guide.side}.`;
  if (st.view === 'front' && guide.front) return `Вижу спереди: проверяю ${guide.front}.`;
  return `Вижу ${VIEW_RU[st.view]}: ${hold ? 'только засекаю время' : 'только считаю'} — встаньте ровно боком.`;
}

const SessionCard: React.FC<{ st: TrainerState | null; send: (m: object) => void; online: boolean }> = ({ st, send, online }) => {
  const exercises = st ? Object.entries(st.exercises) : [];
  const [exercise, setExercise] = useState(st?.config.exercise ?? 'goblet_squat');
  useEffect(() => { if (st && !st.active) setExercise(st.config.exercise); }, [st?.config.exercise]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st) {
    return (
      <div className="feature-card">
        <h3><span>Тренировка</span><span className="badge off">нет связи</span></h3>
        <p className="hint">Нода trainer на роботе не отвечает (робот спит, не на связи или старая версия ПО на Pi).</p>
      </div>
    );
  }
  const hold = kindOf(st) === 'hold';
  const guide = GUIDES[st.exercise] ?? GUIDE_FALLBACK;
  const name = st.exercises[st.exercise]?.name ?? st.exercise;
  const done = lastSet(st);
  const pick = (id: string) => {
    setExercise(id);
    send({ cmd: 'config', exercise: id });  // настройки под упражнение (повторы или удержание)
  };
  return (
    <div className="feature-card">
      <h3>
        <span>Тренировка</span>
        <span className={`badge ${st.active ? 'ok' : 'off'}`}>{PHASE_RU[st.phase] ?? st.phase}</span>
      </h3>
      {st.phase === 'done' && st.history.length > 0 && (
        <p className="op-trainer-total" role="status">
          Тренировка окончена: подходов {st.history.length},{' '}
          {hold
            ? `всего ${Math.round(st.history.reduce((n, h) => n + (h.hold_s ?? 0), 0))} с в планке`
            : `повторов ${st.history.reduce((n, h) => n + (h.reps ?? 0), 0)}, хороших ${st.history.reduce((n, h) => n + (h.good ?? 0), 0)}`}.
          Подробно — в таблице «Подходы».
        </p>
      )}
      {!st.active && (
        <div className="row">
          <label className="op-trainer-field">
            <span>Упражнение</span>
            <select value={exercise} onChange={e => pick(e.target.value)}>
              {exercises.map(([id, ex]) => <option key={id} value={id}>{ex.name}</option>)}
            </select>
          </label>
          <button type="button" className="op-btn primary" disabled={!online}
                  onClick={() => send({ cmd: 'start', exercise })}>Начать</button>
        </div>
      )}
      {st.active && (
        <>
          <p className="op-trainer-title">{name}</p>
          <div className="op-trainer-counters" role="status" aria-live="polite">
            <div><small>{done ? 'Подход готов' : 'Подход'}</small><b>{st.set}<i> из {st.sets}</i></b></div>
            {hold ? (
              <div>
                <small>{done ? 'Продержались' : 'Держим'}</small>
                <b>{Math.floor(done ? done.hold_s ?? 0 : st.hold ?? 0)}<i> из {st.hold_s ?? st.config.hold_s} с</i></b>
              </div>
            ) : (
              <>
                <div><small>Повторы</small><b>{done ? done.reps : st.reps}<i> из {st.target_reps}</i></b></div>
                <div><small>Хороших</small><b>{done ? done.good : st.good}</b></div>
              </>
            )}
            {st.phase === 'rest' && <div><small>Отдых</small><b>{st.rest_left ?? 0}<i> с</i></b></div>}
          </div>
          <p className="hint">{sessionHint(st, hold, guide)}</p>
          <div className="row">
            {st.phase === 'rest' && <button type="button" className="op-btn" onClick={() => send({ cmd: 'next' })}>Следующий подход сейчас</button>}
            <button type="button" className="op-btn danger" onClick={() => send({ cmd: 'stop' })}>Закончить</button>
          </div>
        </>
      )}
    </div>
  );
};

const Kv: React.FC<{ label: string; bad?: boolean; children: React.ReactNode }> = ({ label, bad, children }) => (
  <div className="op-kv"><span>{label}</span><b className={bad ? 'bad' : ''}>{children}</b></div>
);

const RepCard: React.FC<{ rep: TrainerRep }> = ({ rep }) => {
  const m = rep.metrics;
  const has = (f: string) => rep.faults.includes(f);
  const lunge = typeof m.front_knee === 'number';
  return (
    <div className="feature-card">
      <h3>
        <span>Повтор {rep.n} (подход {rep.set})</span>
        <span className={`badge ${rep.faults.length ? 'warn' : 'ok'}`}>{rep.faults.length ? 'поправить' : 'хорошо'}</span>
      </h3>
      {rep.phrases.length > 0 && <ul className="op-trainer-faults">{rep.phrases.map(p => <li key={p}>{p}</li>)}</ul>}
      <Kv label="Ракурс">{VIEW_RU[rep.view]}</Kv>
      {rep.view === 'side' && lunge && (
        <>
          <Kv label="Переднее колено" bad={has('shallow') || has('knee_over_toe')}>
            {num(m.front_knee)}° <small>({m.front === 'right' ? 'правая' : 'левая'} нога, цель ~90°)</small>
          </Kv>
          <Kv label="Заднее колено" bad={has('shallow')}>{num(m.back_knee)}°</Kv>
          <Kv label="Голень вперёд" bad={has('knee_over_toe')}>{num(m.shin_deg)}°</Kv>
          <Kv label="Корпус от вертикали" bad={has('lean')}>{num(m.lean_deg)}°</Kv>
        </>
      )}
      {rep.view === 'side' && !lunge && (
        <>
          <Kv label="Глубина" bad={has('shallow')}>{depthText(m.thigh_deg)}</Kv>
          <Kv label="Угол в колене">{num(m.knee_angle)}°</Kv>
          <Kv label="Корпус от вертикали" bad={has('lean')}>{num(m.lean_deg)}°</Kv>
          <Kv label="Гиря впереди голеностопа" bad={has('weight_forward')}>{num(m.weight_ahead, 2)} голени</Kv>
          <Kv label="Подъём пятки" bad={has('heels_up')}>{num(m.heel_rise, 2)} голени</Kv>
        </>
      )}
      {rep.view === 'front' && typeof m.knee_ratio === 'number' && (
        <>
          <Kv label="Колени / стопы (ширина)" bad={has('knees_in')}>{num(m.knee_ratio, 2)}</Kv>
          {typeof m.stance === 'number' && <Kv label="Стопы / плечи (ширина)" bad={has('feet_wide')}>{num(m.stance, 2)}</Kv>}
        </>
      )}
      <Kv label="Темп: вниз / вверх">{num(m.down_s, 1)} / {num(m.up_s, 1)} с</Kv>
    </div>
  );
};

// Планка: замеры сейчас (пока держит) или итог последнего удержания
const HoldCard: React.FC<{ st: TrainerState }> = ({ st }) => {
  const live = st.phase === 'set' && st.live ? st.live : null;
  const last = !isRep(st.last) ? st.last : null;
  if (!live && !last) return null;
  const m = live ? live.metrics : last!.metrics;
  const faults = live ? live.faults : last!.faults;
  const view = typeof m.view === 'string' ? (m.view as TrainerView) : null;
  return (
    <div className="feature-card">
      <h3>
        <span>{live ? 'Сейчас' : `Удержание (подход ${last!.set}): ${num(last!.hold_s)} с`}</span>
        <span className={`badge ${faults.length ? 'warn' : 'ok'}`}>{faults.length ? 'поправить' : 'хорошо'}</span>
      </h3>
      {live && faults.length > 0 && <ul className="op-trainer-faults">{faults.map(f => <li key={f}>{FAULT_RU[f] ?? f}</li>)}</ul>}
      {!live && last!.phrases.length > 0 && <ul className="op-trainer-faults">{last!.phrases.map(p => <li key={p}>{p}</li>)}</ul>}
      <Kv label="Ракурс">{view ? VIEW_RU[view] : '—'}</Kv>
      {view === 'side' ? (
        <>
          <Kv label={live ? 'Таз относительно линии плечо–пятки' : 'Таз в конце удержания'} bad={!!live && faults.length > 0}>
            {hipsText(m.hips_pct)}
          </Kv>
          <Kv label={live ? 'Угол плечо–таз–голеностоп' : 'Угол плечо–таз–голеностоп в конце'}>{num(m.body_deg)}°</Kv>
        </>
      ) : <p className="hint">Положение таза видно только сбоку — сейчас робот лишь засекает время.</p>}
    </div>
  );
};

const HistoryCard: React.FC<{ st: TrainerState | null }> = ({ st }) => {
  if (!st?.history.length) return null;
  const hold = kindOf(st) === 'hold';
  const faults = (f: Record<string, number>) => Object.entries(f).sort((a, b) => b[1] - a[1])
    .map(([k, n]) => (hold ? `${FAULT_RU[k] ?? k} ${num(n)} с` : `${FAULT_RU[k] ?? k} ×${n}`)).join(', ') || '—';
  return (
    <div className="feature-card">
      <h3><span>Подходы</span></h3>
      <table className="op-trainer-table">
        <thead>
          {hold
            ? <tr><th>Подход</th><th>Удержание, с</th><th>Цель, с</th><th>Замечания</th></tr>
            : <tr><th>Подход</th><th>Повторы</th><th>Хороших</th><th>Замечания</th></tr>}
        </thead>
        <tbody>
          {st.history.map(s => (
            <tr key={s.set}>
              <td>{s.set}</td>
              <td>{hold ? num(s.hold_s) : s.reps}</td>
              <td>{hold ? s.target_s : s.good}</td>
              <td>{faults(s.faults)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type NumKey = 'target_reps' | 'hold_s' | 'sets' | 'rest_s' | 'set_end_s';
const NUM_FIELDS: Record<TrainerKind, [NumKey, string, number, number][]> = {
  reps: [['target_reps', 'Повторов в подходе', 1, 50], ['sets', 'Подходов', 1, 10],
         ['rest_s', 'Отдых, с', 10, 600], ['set_end_s', 'Подход кончился, если нет повторов, с', 5, 120]],
  hold: [['hold_s', 'Удержание, с', 10, 300], ['sets', 'Подходов', 1, 10], ['rest_s', 'Отдых, с', 10, 600]],
};
const EMPTY: Record<NumKey, string> = { target_reps: '', hold_s: '', sets: '', rest_s: '', set_end_s: '' };

const SettingsCard: React.FC<{ st: TrainerState | null; send: (m: object) => void }> = ({ st, send }) => {
  const cfg = st?.config ?? null;
  const [draft, setDraft] = useState<Record<NumKey, string>>(EMPTY);
  useEffect(() => {
    if (cfg) setDraft({ target_reps: `${cfg.target_reps}`, hold_s: `${cfg.hold_s ?? 30}`, sets: `${cfg.sets}`,
                        rest_s: `${cfg.rest_s}`, set_end_s: `${cfg.set_end_s}` });
  }, [cfg?.target_reps, cfg?.hold_s, cfg?.sets, cfg?.rest_s, cfg?.set_end_s]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st || !cfg) return null;
  const kind = kindOf(st, cfg.exercise);
  const commit = (k: NumKey) => {
    const v = Number(draft[k]);
    if (Number.isFinite(v) && v !== (cfg as TrainerConfig)[k]) send({ cmd: 'config', [k]: v });
  };
  return (
    <div className="feature-card">
      <h3><span>Настройки: {st.exercises[cfg.exercise]?.name.toLowerCase() ?? cfg.exercise}</span></h3>
      <div className="op-trainer-grid">
        {NUM_FIELDS[kind].map(([k, label, min, max]) => (
          <label key={k} className="op-trainer-field">
            <span>{label}</span>
            <input type="number" min={min} max={max} value={draft[k]}
                   onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
                   onBlur={() => commit(k)} onKeyDown={e => e.key === 'Enter' && commit(k)} />
          </label>
        ))}
        <label className="op-trainer-field">
          <span>Подсказки голосом</span>
          <select value={cfg.voice} onChange={e => send({ cmd: 'config', voice: e.target.value })}>
            {VOICE_RU.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>
      <label className="op-check">
        <input type="checkbox" checked={cfg.count} onChange={e => send({ cmd: 'config', count: e.target.checked })} />
        {kind === 'hold' ? 'Говорить время каждые 10 секунд' : 'Считать повторы вслух'}
      </label>
      <p className="hint">
        Настройки хранятся на роботе.{' '}
        {kind === 'hold'
          ? 'Замечание звучит, когда ошибка держится полторы секунды, и повторяется не чаще раза в 8 секунд.'
          : 'Одно и то же замечание робот не повторяет два повтора подряд.'}
      </p>
    </div>
  );
};

const HowCard: React.FC<{ st: TrainerState | null }> = ({ st }) => {
  const id = st?.config.exercise ?? '';
  const ex = st ? st.exercises[id] : null;
  const guide = GUIDES[id] ?? GUIDE_FALLBACK;
  const hold = st ? kindOf(st, id) === 'hold' : false;
  return (
    <div className="feature-card">
      <h3><span>{ex ? `Как делать: ${ex.name.toLowerCase()}` : 'Как делать'}</span></h3>
      {ex && <ul className="op-trainer-tips">{ex.tips.map(t => <li key={t}>{t}</li>)}</ul>}
      <p className="hint">
        <b>Куда встать.</b> {guide.place} Сбоку робот проверяет {guide.side}
        {guide.front ? `; лицом к роботу — ${guide.front}` : ''}; с других сторон — {hold ? 'только засекает время' : 'только считает'}.
      </p>
      {guide.notChecked && (
        <p className="hint">
          Не проверяется: {guide.notChecked} — их не видно по 17 точкам скелета. Это подсказки, а не медицинский контроль.
        </p>
      )}
    </div>
  );
};

const TrainerPanel: React.FC = () => {
  const { st, send, online } = useTrainer();
  return (
    <div className="op-people-layout">
      <div className="feature-card op-people-cam">
        <h3><span>Камера 1 сейчас</span></h3>
        <CameraView cam="0" className="op-people-cam-view" />
        <p className="op-people-cam-note">Скелет поверх кадра — то, по чему робот судит о движении.</p>
      </div>
      <div className="op-people-panels">
        <SessionCard st={st} send={send} online={online} />
        {st && kindOf(st) === 'hold' ? <HoldCard st={st} /> : isRep(st?.last ?? null) && <RepCard rep={st!.last as TrainerRep} />}
        <HistoryCard st={st} />
        <SettingsCard st={st} send={send} />
        <HowCard st={st} />
      </div>
    </div>
  );
};

export default TrainerPanel;
